import { MarketAlertMode } from '@/utils/enums';

import { baseApi } from './baseApi';
import { MarketActivityFilterFields } from './marketActivityApi';
import { ApiTags, CommonTagIds } from './tags';

/**
 * A stored filter snapshot as the API returns it.
 *
 * Every key is always present, with unset filters as explicit `null` rather
 * than omitted: the backend types this column as a fully-populated
 * `MarketActivityFilters` record and serializes it with Jackson's default
 * property inclusion. Read these with `== null` / `??` — an `undefined`-only
 * check lets `null` through and stringifies to `"null"`.
 *
 * Requests go the other way (see `CreateMarketAlertSubscriptionParams`), which
 * omits unset filters instead.
 */
export type StoredMarketActivityFilters = {
  [K in keyof MarketActivityFilterFields]?:
    MarketActivityFilterFields[K] | null;
};

export type MarketAlertSubscription = {
  id: number;
  mode: MarketAlertMode;
  filters: StoredMarketActivityFilters;
  actingCompanyId: string | null;
  actingSideView: string | null;
  created: string; // ISO-8601
};

export type CreateMarketAlertSubscriptionParams = {
  mode: MarketAlertMode;
  /**
   * The filter snapshot to alert on. Optional because only `MANUAL`
   * subscriptions have one: a sync mode derives its scope from the caller's
   * own positions, and the `chk_mas_filters` constraint asserts
   * `(mode = 'MANUAL') = (filters IS NOT NULL)`, so sending a snapshot
   * alongside a sync mode is rejected.
   */
  filters?: MarketActivityFilterFields;
  companyId: string;
  sideView: string;
};

/**
 * The endpoint is user-scoped: it deletes any alert the caller owns, whatever
 * company and side they act as now, so it takes no acting-context headers.
 */
export type DeleteMarketAlertSubscriptionParams = {
  id: number;
};

const BASE_URL = '/api/v1/market-alert-subscriptions';

export const marketAlertSubscriptionsApi = baseApi.injectEndpoints({
  endpoints: build => ({
    getMarketAlertSubscriptions: build.query<MarketAlertSubscription[], void>({
      query: () => ({
        url: BASE_URL,
      }),
      providesTags: [
        { type: ApiTags.ALERT_SUBSCRIPTIONS, id: CommonTagIds.LIST },
      ],
    }),
    createMarketAlertSubscription: build.mutation<
      MarketAlertSubscription,
      CreateMarketAlertSubscriptionParams
    >({
      query: ({ mode, filters, companyId, sideView }) => ({
        url: BASE_URL,
        method: 'POST',
        // Built per mode rather than always spreading `filters`, so a sync
        // subscription cannot carry a snapshot the database would reject —
        // even if a caller passes one.
        body: mode === MarketAlertMode.MANUAL ? { mode, filters } : { mode },
        headers: {
          'X-Company-Id': companyId,
          'X-Side-View': sideView,
        },
      }),
      invalidatesTags: [
        { type: ApiTags.ALERT_SUBSCRIPTIONS, id: CommonTagIds.LIST },
      ],
    }),
    deleteMarketAlertSubscription: build.mutation<
      void,
      DeleteMarketAlertSubscriptionParams
    >({
      query: ({ id }) => ({
        url: `${BASE_URL}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: ApiTags.ALERT_SUBSCRIPTIONS, id: CommonTagIds.LIST },
      ],
    }),
  }),
});

export const {
  useGetMarketAlertSubscriptionsQuery,
  useCreateMarketAlertSubscriptionMutation,
  useDeleteMarketAlertSubscriptionMutation,
} = marketAlertSubscriptionsApi;
